/**
 * Step 9+10: Nudge banner — bottom-right toast, no layout break.
 *
 * Appears only when nextBestAction !== "none".
 * Client-side guards mirror server guards: cooldown + max nudges.
 * Auto-dismisses after 12s. Tracks shown/click.
 */

import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { usePredictiveUx, type NextBestAction } from "@/hooks/usePredictiveUx";
import { getUxFlags } from "@/lib/uxFlags";
import {
  trackNudgeShown,
  trackNudgeClick,
  trackGoalStart,
  trackGoalComplete,
  trackWhatsappClick,
} from "@/lib/uxTracking";

// ─── Client-side nudge guard (mirrors server) ─────────────────────────────────

const CLIENT_MAX_NUDGES = 2;
const CLIENT_COOLDOWN_MS = 120_000; // 2 min
const AUTO_DISMISS_MS = 12_000;

const STORAGE_NUDGE_COUNT = "ux_nudge_count";
const STORAGE_NUDGE_LAST_TS = "ux_nudge_last_ts";

function getClientNudgeCount(): number {
  try { return parseInt(sessionStorage.getItem(STORAGE_NUDGE_COUNT) ?? "0", 10) || 0; } catch { return 0; }
}
function incClientNudgeCount(): void {
  try { sessionStorage.setItem(STORAGE_NUDGE_COUNT, String(getClientNudgeCount() + 1)); } catch {}
}
function getLastNudgeTs(): number {
  try { return parseInt(sessionStorage.getItem(STORAGE_NUDGE_LAST_TS) ?? "0", 10) || 0; } catch { return 0; }
}
function setLastNudgeTs(): void {
  try { sessionStorage.setItem(STORAGE_NUDGE_LAST_TS, String(Date.now())); } catch {}
}

function canShowNudge(): boolean {
  if (getClientNudgeCount() >= CLIENT_MAX_NUDGES) return false;
  const lastTs = getLastNudgeTs();
  if (lastTs > 0 && Date.now() - lastTs < CLIENT_COOLDOWN_MS) return false;
  return true;
}

// ─── Nudge content ────────────────────────────────────────────────────────────

import { HAS_WHATSAPP, getWhatsAppLink } from "@/lib/contactConfig";

interface NudgeConfig {
  id: string;
  title: string;
  body: string;
  cta: string;
}

const NUDGE_CONFIGS: Record<Exclude<NextBestAction, "none">, NudgeConfig> = {
  nudge_whatsapp_soft: {
    id: "nudge_whatsapp_soft",
    title: "Une question ?",
    body: "Nos coordinateurs répondent en quelques minutes.",
    cta: "Contacter via WhatsApp",
  },
  nudge_form_soft: {
    id: "nudge_form_soft",
    title: "Voir le protocole",
    body: "Découvrez les étapes et les tarifs en toute transparence.",
    cta: "Voir le protocole & devis",
  },
  nudge_access_soft: {
    id: "nudge_access_soft",
    title: "Prêt à commencer ?",
    body: "Accédez à votre espace d'évaluation personnalisée.",
    cta: "Accéder à mon espace",
  },
  handover_human: {
    id: "handover_human",
    title: "Besoin d'aide ?",
    body: "Un coordinateur peut vous guider personnellement.",
    cta: "Parler à un coordinateur",
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function NudgeBanner() {
  const [enabled, setEnabled] = useState(false);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [shownFor, setShownFor] = useState<NextBestAction | null>(null);
  const [, navigate] = useLocation();
  const { nextBestAction, isActive } = usePredictiveUx();
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shownRef = useRef(false);

  // Fetch flags once
  useEffect(() => {
    getUxFlags().then((f) => setEnabled(f.adaptiveUxEnabled && f.uxNudgesEnabled));
  }, []);

  // Show nudge when conditions met
  useEffect(() => {
    if (!enabled || !isActive || dismissed || shownRef.current) return;
    if (nextBestAction === "none") return;
    if (nextBestAction === "nudge_whatsapp_soft" && !HAS_WHATSAPP) return;
    if (!canShowNudge()) return;

    // Small delay so page is fully rendered
    const t = setTimeout(() => {
      shownRef.current = true;
      setShownFor(nextBestAction);
      setVisible(true);
      incClientNudgeCount();
      setLastNudgeTs();
      trackNudgeShown(nextBestAction);

      // Auto-dismiss
      dismissTimer.current = setTimeout(() => setVisible(false), AUTO_DISMISS_MS);
    }, 3000);

    return () => clearTimeout(t);
  }, [enabled, isActive, nextBestAction, dismissed]);

  // Cleanup on unmount
  useEffect(() => () => { if (dismissTimer.current) clearTimeout(dismissTimer.current); }, []);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
  };

  const handleClick = (action: NextBestAction) => {
    trackNudgeClick(action);
    setVisible(false);
    setDismissed(true);

    switch (action) {
      case "nudge_whatsapp_soft":
        trackGoalStart("goal_whatsapp");
        trackWhatsappClick();
        if (HAS_WHATSAPP) {
          window.open(getWhatsAppLink(), "_blank", "noopener,noreferrer");
          trackGoalComplete("goal_whatsapp");
        }
        break;
      case "nudge_form_soft":
        trackGoalStart("goal_form_submit");
        // Scroll to form section if present, or navigate
        {
          const section = document.querySelector("[data-testid='section-steps']");
          if (section) section.scrollIntoView({ behavior: "smooth" });
          else navigate("/access");
        }
        break;
      case "nudge_access_soft":
        trackGoalStart("goal_access_complete");
        navigate("/access");
        break;
      case "handover_human":
        trackGoalStart("goal_chat_handover");
        navigate("/evaluation");
        break;
    }
  };

  if (!visible || !shownFor || shownFor === "none") return null;
  const cfg = NUDGE_CONFIGS[shownFor];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="nudge-banner"
          initial={{ opacity: 0, y: 16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.97 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 right-4 z-[9998] w-72 rounded-xl border border-border/40 bg-background/95 backdrop-blur-sm shadow-xl p-4"
          role="complementary"
          aria-label="Suggestion personnalisée"
          data-testid="nudge-banner"
          data-ux-nudge={cfg.id}
        >
          {/* Dismiss */}
          <button
            onClick={handleDismiss}
            className="absolute top-2.5 right-2.5 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Fermer"
            data-testid="nudge-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          {/* Content */}
          <p className="text-xs font-semibold text-foreground mb-0.5 pr-5">{cfg.title}</p>
          <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{cfg.body}</p>

          <button
            onClick={() => handleClick(shownFor)}
            className="w-full text-xs font-medium py-1.5 px-3 rounded-lg bg-primary/10 text-primary hover:bg-primary/15 border border-primary/20 transition-colors text-center"
            data-testid="nudge-cta"
          >
            {cfg.cta}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
